import { Box, Button, useTheme } from "@mui/material";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import { useState } from "react";
import { ResponsiveTable } from "@/components/Responsive";
import { type ReassistMonthlyBilling } from "../types";
import { downloadBillingCsv } from "../utils/billingCsv";
import { formatMonth } from "../utils/format";
import { responsiveTableWrapSx } from "../utils/tokens";
import { AmountStatusChip } from "./AmountText";
import { reassistButtonSx } from "./ReassistButton";
import { ReassistBillingItemsDialog } from "./ReassistBillingItemsDialog";
import { EmptyState } from "./EmptyState";

interface Props {
  rows: ReassistMonthlyBilling[];
}

/**
 * 表の列。PC の表の幅に収まるよう、書類数・確定額・暫定額の内訳は表に出さず、明細のダイアログのまとめに出す。
 */
const HEADERS = [
  "対象月",
  "依頼数",
  "依頼の合計（税込）",
  "黒塗り購入（税込）",
  "請求合計（税込）",
  "明細",
];
/** SP のカードに出す列。依頼数は「詳細」へ。明細のボタンは SP でもカードに出す。 */
const SUMMARY_COLUMNS = [0, 2, 3, 4, 5];

/** 暫定額の表示。暫定が無い月は「なし」。 */
const provisionalText = (row: ReassistMonthlyBilling): string => {
  if (row.provisionalCount === 0) return "なし";
  const amount = `${row.provisionalTotal.toLocaleString()}円`;
  // 税込額を求められない暫定は、金額に含めていない。含めていない件数を括弧の中に書き添える
  return row.untaxedProvisionalCount > 0
    ? `${amount}（${row.provisionalCount}件。税込額が未定の${row.untaxedProvisionalCount}件を除く）`
    : `${amount}（${row.provisionalCount}件）`;
};

/** 合計。暫定を含む月は「暫定」のチップが伝えるため、金額には「約」を付けない。 */
const totalText = (row: ReassistMonthlyBilling): string =>
  `${row.total.toLocaleString()}円`;

/** 黒塗り購入の表示。購入が無い月は「なし」。税込額に件数を添える（クレジットは税別なので、消費税を足した額）。 */
const purchaseText = (row: ReassistMonthlyBilling): string =>
  row.purchaseCount === 0
    ? "なし"
    : `${row.purchaseTotal.toLocaleString()}円（${row.purchaseCount}件）`;

/**
 * 月別の依頼数・請求額（依頼・黒塗り購入・合計）。暫定を含む月は、その旨を表示する。各月の「明細」から、その月の請求明細を開く。
 * 表は ResponsiveTable（PC=表、SP=カード）。
 */
export const ReassistBillingTable: React.FC<Props> = ({ rows }) => {
  const theme = useTheme();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const selected = rows.find((row) => row.month === selectedMonth) ?? null;
  if (rows.length === 0) {
    return <EmptyState>請求はまだありません</EmptyState>;
  }

  const tableRows = rows.map((row) => [
    <Box key="m" component="span" sx={{ whiteSpace: "nowrap" }}>
      {formatMonth(row.month)}
    </Box>,
    `${row.requestCount}`,
    <Box
      key="t"
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        fontWeight: 700,
      }}
    >
      {row.provisionalCount > 0 && <AmountStatusChip isProvisional />}
      <span>{totalText(row)}</span>
    </Box>,
    <Box key="pu" component="span" sx={{ color: "text.secondary" }}>
      {purchaseText(row)}
    </Box>,
    <Box
      key="g"
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        fontWeight: 700,
      }}
    >
      {/* 依頼の暫定額を含む月は、請求合計も暫定 */}
      {row.provisionalCount > 0 && <AmountStatusChip isProvisional />}
      <span>{`${row.grandTotal.toLocaleString()}円`}</span>
    </Box>,
    // 明細が無い月はボタンを出さない
    row.items.length > 0 || row.purchases.length > 0 ? (
      <Box
        key="items"
        component="span"
        sx={{ display: "inline-flex", gap: 1, flexWrap: "wrap" }}
      >
        <Button
          variant="outlined"
          size="small"
          className="TapExpanded"
          startIcon={<ListAltOutlined />}
          aria-label={`${formatMonth(row.month)}の請求明細`}
          onClick={() => {
            setSelectedMonth(row.month);
          }}
          sx={reassistButtonSx("outlined", "small")}
        >
          明細
        </Button>
        <Button
          variant="outlined"
          size="small"
          className="TapExpanded"
          startIcon={<FileDownloadOutlined />}
          aria-label={`${formatMonth(row.month)}の請求明細をCSVでダウンロード`}
          onClick={() => {
            downloadBillingCsv(row);
          }}
          sx={reassistButtonSx("outlined", "small")}
        >
          CSV
        </Button>
      </Box>
    ) : null,
  ]);

  return (
    <Box
      sx={{
        ...responsiveTableWrapSx(theme),
        // 数値の列（2〜5列目）と明細のボタンは右揃え
        "& .MuiTableCell-root:nth-of-type(n+2)": { textAlign: "right" },
      }}
    >
      <ResponsiveTable
        title="月別の請求"
        headers={HEADERS}
        rows={tableRows}
        summaryColumns={SUMMARY_COLUMNS}
      />
      <ReassistBillingItemsDialog
        billing={selected}
        provisionalText={provisionalText}
        totalText={totalText}
        purchaseText={purchaseText}
        onClose={() => {
          setSelectedMonth(null);
        }}
      />
    </Box>
  );
};
